import { z } from 'zod';

/** IoT thing group suffix: lowercase, dots → hyphens, underscores → hyphens */
export function toThingGroupSuffix(adapterKey: string): string {
  return adapterKey.toLowerCase().replace(/\./g, '-').replace(/_/g, '-');
}

/** Profile keys from `greengrass-deploy-tools/deploy-profiles.json` (keep in sync when profiles are added). */
export const DeployProfileKeySchema = z.enum([
  'edge-device-guard',
  'axon-orchestrator',
  'matrix-orchestrator',
  'woxu-ls1000',
  'efem-csv-stub-adapter-1',
  'efem-csv-stub-adapter-2',
]);
export type DeployProfileKey = z.infer<typeof DeployProfileKeySchema>;

export type GatewayDeviceCategory = 'efem-gateway' | 'location-gateway';

export function edgeThingGroup(): string {
  return 'cci-edge';
}

export function tenantThingGroup(tenantId: string): string {
  return `cci-tenant-${tenantId}`;
}

export function efemCategoryThingGroup(): string {
  return 'cci-axon';
}

export function locationCategoryThingGroup(): string {
  return 'cci-matrix';
}

export function efemUnassignedThingGroup(): string {
  return 'cci-axon-unassigned';
}

export function locationUnassignedThingGroup(): string {
  return 'cci-matrix-unassigned';
}

export function efemAdapterThingGroup(adapterKey: string): string {
  return `cci-axon-adapter-${toThingGroupSuffix(adapterKey)}`;
}

export function locationAdapterThingGroup(adapterKey: string): string {
  return `cci-matrix-adapter-${toThingGroupSuffix(adapterKey)}`;
}

/** Flat adapter leaf name when registry already provides `thingGroupSuffix`. */
export function adapterThingGroupNameFromSuffix(
  kind: 'efem' | 'location',
  thingGroupSuffix: string,
): string {
  return kind === 'efem'
    ? `cci-axon-adapter-${thingGroupSuffix}`
    : `cci-matrix-adapter-${thingGroupSuffix}`;
}

export interface ThingGroupsForGatewayParams {
  tenantId: string;
  deviceCategory: GatewayDeviceCategory;
  efemAdapterKey?: string | null;
  locationAdapterKey?: string | null;
}

/** Static catalogue groups (no tenant id, no per-adapter leaves). */
export function catalogueStaticThingGroupNames(): string[] {
  return [
    edgeThingGroup(),
    efemCategoryThingGroup(),
    locationCategoryThingGroup(),
    efemUnassignedThingGroup(),
    locationUnassignedThingGroup(),
  ];
}

export function thingGroupsForGateway(params: ThingGroupsForGatewayParams): string[] {
  const { tenantId, deviceCategory, efemAdapterKey, locationAdapterKey } = params;
  const groups: string[] = [edgeThingGroup(), tenantThingGroup(tenantId)];

  if (deviceCategory === 'efem-gateway') {
    groups.push(efemCategoryThingGroup());
    if (efemAdapterKey) {
      groups.push(efemAdapterThingGroup(efemAdapterKey));
    } else {
      groups.push(efemUnassignedThingGroup());
    }
  } else {
    groups.push(locationCategoryThingGroup());
    if (locationAdapterKey) {
      groups.push(locationAdapterThingGroup(locationAdapterKey));
    } else {
      groups.push(locationUnassignedThingGroup());
    }
  }

  return groups;
}

/** Flat `cci-*` groups managed by provision template and assignment sync. */
export function isManagedThingGroupName(groupName: string): boolean {
  return groupName.startsWith('cci-');
}

export type DeployTargetKind = 'edge' | 'efem' | 'location' | 'efem-adapter' | 'location-adapter';

export function greengrassDeployTargetGroup(
  deployTargetKind: DeployTargetKind,
  adapterKey?: string,
): string {
  switch (deployTargetKind) {
    case 'edge':
      return edgeThingGroup();
    case 'efem':
      return efemCategoryThingGroup();
    case 'location':
      return locationCategoryThingGroup();
    case 'efem-adapter':
      if (!adapterKey) throw new Error('efem-adapter deploy target requires adapterKey');
      return efemAdapterThingGroup(adapterKey);
    case 'location-adapter':
      if (!adapterKey) throw new Error('location-adapter deploy target requires adapterKey');
      return locationAdapterThingGroup(adapterKey);
    default: {
      const _exhaustive: never = deployTargetKind;
      throw new Error(`Unknown deploy target kind: ${_exhaustive}`);
    }
  }
}
